/**
 * IP Intel Report — Step 2 of 2, the technology intake, at
 * /ip-intel-report/intake/:token.
 *
 * Reached straight after a verified payment (and from the "order paid"
 * e-mail). The token is the only key: no sign-in, because most buyers check
 * out as guests. What the visitor declares here is what the analysis is
 * benchmarked on — a Cosmos listing or their own IP, plus the compulsory TRL
 * and stage of development — and submitting it starts the 7-day clock.
 *
 * States: loading · not found · awaiting payment (polls briefly while the
 * webhook lands) · form · read-only (with Edit while the order still allows
 * it — status paid or clarification) · success · delivered / cancelled.
 * Limits (5 files, 25 MB, the extension list) are enforced here for fast
 * feedback; the server re-checks all of them.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePageTitle } from '../../utils/pageTitle';
import { money } from '../../utils/money';
import { IpIntelShell } from './ipintel/Shell';
import { ipIntelApi, IntakeOrder, IntakePayload, submitIntake, TechHit, techHits } from './ipintel/api';
import { ipTrack, ipClip, useScrollDepth } from './ipintel/track';
import {
  CONTACT_EMAIL, STAGES, TRL_LEVELS, UPLOAD_EXTS, UPLOAD_MAX_FILES, UPLOAD_MAX_MB,
} from './ipintel/content';

type Load = 'loading' | 'notfound' | 'error' | 'ready';
type Source = 'cosmos' | 'own';

const EDITABLE = ['paid', 'clarification'];
const PAID = ['paid', 'intake_submitted', 'clarification', 'in_progress', 'delivered'];

const extOf = (name: string) => (name.split('.').pop() || '').toLowerCase();
const mb = (bytes?: number) => (bytes == null ? '' : bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
/** What the server can resolve without a pick: a UTECH id (alone or inside
 *  a link), a numeric listing id, or a URL (ip_intel.resolve_cosmos). */
const UTECH_ID_RE = /UTECH-[A-Z0-9]+-\d{3,}/i;
const looksLikeRef = (s: string) => UTECH_ID_RE.test(s) || /^\d{1,12}$/.test(s) || /^https?:\/\//i.test(s);

/** The stored intake is nested ({cosmos: {...}} / {own: {...}}); older or
 *  hand-made rows may be flat. Read either. */
const readIntake = (raw: Record<string, any> | null | undefined) => {
  const i = raw || {};
  const c = (i.cosmos && typeof i.cosmos === 'object') ? i.cosmos : {};
  const o = (i.own && typeof i.own === 'object') ? i.own : {};
  const isCosmos = i.source === 'cosmos';
  return {
    source: (i.source === 'own' ? 'own' : i.source === 'cosmos' ? 'cosmos' : '') as '' | Source,
    cosmosId: String(c.utech_technology_id || i.utech_technology_id || ''),
    cosmosTitle: String(c.title || i.cosmos_title || (isCosmos ? i.title : '') || ''),
    cosmosUniversity: String(c.university_name || i.cosmos_university || i.university_name || ''),
    cosmosInput: String(c.input || i.cosmos_ref || ''),
    title: String(o.title || (!isCosmos ? i.title : '') || ''),
    summary: String(o.summary || i.summary || ''),
    patent: String(o.patent_number || i.patent_number || ''),
    trl: Number(i.trl) || 0,
    stage: String(i.stage_of_development || ''),
    stageNotes: String(i.stage_notes || ''),
    markets: String(i.target_markets || ''),
    questions: String(i.key_questions || ''),
    confirm: !!i.contact_confirm,
  };
};

const fmtDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

interface PendingFile { id: string; file: File; progress: number }

/** The contact address as a tracked mailto link; `position` says which state of the page it was on. */
const MailLink: React.FC<{ position: string; orderRef?: string; subject?: string }> = ({ position, orderRef, subject }) => (
  <a
    href={`mailto:${CONTACT_EMAIL}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`}
    onClick={() => ipTrack('ipintel_click_contact', { position, ...(orderRef ? { order_ref: orderRef } : {}) })}
    style={{ color: 'var(--ipr-primary-600)', fontWeight: 600 }}
  >{CONTACT_EMAIL}</a>
);

/** "Back to IP Intel Report", tracked with the state it was clicked from. */
const BackLink: React.FC<{ from: string }> = ({ from }) => (
  <a className="ipr-btn-secondary" href="/ip-intel-report" onClick={() => ipTrack('ipintel_intake_back', { from })}>Back to IP Intel Report</a>
);

// ---------------------------------------------------------------------------

const Card: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="ipr-card" style={{ padding: 28 }}>
    <div style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 18, fontWeight: 700, color: 'var(--ipr-heading)', marginBottom: hint ? 4 : 18 }}>{title}</div>
    {hint && <div style={{ fontSize: 14, color: 'var(--ipr-gray-300)', marginBottom: 18, lineHeight: 1.55 }}>{hint}</div>}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{children}</div>
  </div>
);

const FieldErr: React.FC<{ id: string; msg?: string }> = ({ id, msg }) =>
  msg ? <div id={id} className="ipr-field-error" role="alert">{msg}</div> : null;

// ---------------------------------------------------------------------------

// The token straight from the address bar, not react-router's useParams: a
// second react-router copy in the production bundle has no <Router> above
// this page (the same failure took down /ip-intel-report on 2026-09-27).
const tokenFromPath = (): string => {
  try {
    const m = window.location.pathname.match(/^\/ip-intel-report\/intake\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch { return ''; }
};

const IpIntelIntakePage: React.FC = () => {
  usePageTitle('IP Intel Report intake');
  const [token] = useState(tokenFromPath);
  const [load, setLoad] = useState<Load>('loading');
  const [loadError, setLoadError] = useState('');
  const [order, setOrder] = useState<IntakeOrder | null>(null);
  const [editing, setEditing] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const viewTracked = useRef(false);
  const pollCount = useRef(0);

  const fetchOrder = useCallback(async () => {
    const res = await ipIntelApi.getIntake(token);
    if (res.ok) {
      setOrder(res.data);
      setLoad('ready');
      return res.data;
    }
    if (res.status === 404 || res.status === 410) setLoad('notfound');
    else { setLoadError(res.error); setLoad('error'); }
    return null;
  }, [token]);

  useEffect(() => {
    void fetchOrder().then(o => {
      if (viewTracked.current) return;
      viewTracked.current = true;
      ipTrack('ipintel_intake_view', { status: o?.status || 'not_found' });
      if (o && EDITABLE.includes(o.status) && (!o.intake || o.status === 'clarification')) setEditing(true);
    });
  }, [fetchOrder]);

  // The webhook can land a few seconds after the redirect; poll briefly.
  useEffect(() => {
    if (load !== 'ready' || order?.status !== 'created' || pollCount.current >= 12) return;
    const t = setTimeout(() => {
      pollCount.current += 1;
      void fetchOrder().then(o => { if (o && EDITABLE.includes(o.status) && !o.intake) setEditing(true); });
    }, 5000);
    return () => clearTimeout(t);
  }, [load, order, fetchOrder]);

  useScrollDepth('intake', load === 'ready');

  const ref = order?.ref || order?.order_ref || '';
  const status = order?.status || '';
  const editable = order ? (order.editable ?? true) && EDITABLE.includes(status) : false;
  const paid = order ? (order.paid ?? PAID.includes(status)) : false;

  // --------------------------------------------------------------- body
  let body: React.ReactNode;
  if (load === 'loading') {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <span className="ipr-skel" style={{ width: 220, height: 14 }} />
        <span className="ipr-skel" style={{ width: '70%', height: 40 }} />
        <span className="ipr-skel" style={{ width: '100%', height: 220, borderRadius: 12 }} />
      </div>
    );
  } else if (load === 'notfound') {
    body = (
      <Message
        eyebrow="Intake"
        title="We couldn't find that intake link"
        text={<>The link may be mistyped or no longer valid. Use the link in the e-mail we sent after your payment, or write to <MailLink position="intake_notfound" subject="IP Intel Report — intake link" /> with your order reference and we'll send a fresh one.</>}
        from="not_found"
      />
    );
  } else if (load === 'error') {
    body = (
      <Message
        eyebrow="Intake"
        title="We couldn't load your order"
        text={<>{loadError} <button type="button" className="ipr-linkbtn" onClick={() => { ipTrack('ipintel_intake_retry_load'); setLoad('loading'); void fetchOrder(); }} style={{ color: 'var(--ipr-primary-600)', fontWeight: 600 }}>Try again</button></>}
        from="load_error"
      />
    );
  } else if (order && status === 'created') {
    body = (
      <Message
        eyebrow="Step 2 of 2 · technology details"
        title="Waiting for your payment to be confirmed"
        text={<>This usually takes a few seconds. This page refreshes itself{pollCount.current >= 12 ? ' — it has stopped trying for now' : ''}. If you were charged and it does not update, write to <MailLink position="intake_waiting" orderRef={ref} subject={`IP Intel Report ${ref}`} /> quoting <strong>{ref}</strong>.</>}
        action={<button type="button" className="ipr-btn-secondary" onClick={() => { ipTrack('ipintel_intake_check_again', { order_ref: ref }); pollCount.current = 0; void fetchOrder(); }}>Check again</button>}
        from="awaiting_payment"
      />
    );
  } else if (order && (status === 'cancelled' || status === 'refunded')) {
    body = (
      <Message
        eyebrow={`Order ${ref}`}
        title={status === 'refunded' ? 'This order was refunded' : 'This order was cancelled'}
        text={<>Nothing more is needed from you. Questions? Write to <MailLink position="intake_closed" orderRef={ref} subject={`IP Intel Report ${ref}`} />.</>}
        from={status}
      />
    );
  } else if (order && justSubmitted) {
    body = <SubmittedPanel order={order} onView={() => { ipTrack('ipintel_intake_view_submission', { order_ref: ref }); setJustSubmitted(false); window.scrollTo({ top: 0 }); }} />;
  } else if (order && editing && editable) {
    body = (
      <IntakeForm
        token={token}
        order={order}
        onCancelEdit={order.intake ? () => { ipTrack('ipintel_intake_cancel_edit', { status: order.status }); setEditing(false); } : undefined}
        onSubmitted={async () => {
          await fetchOrder();
          setEditing(false);
          setJustSubmitted(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    );
  } else if (order) {
    body = <ReadOnly order={order} editable={editable && !!order.intake} onEdit={() => setEditing(true)} />;
  }

  return (
    <IpIntelShell onSelf={() => { window.location.href = '/ip-intel-report'; }}>
      <section className="ipr-section" style={{ paddingTop: 64, background: 'var(--ipr-gray-50)', minHeight: '70vh' }}>
        <div className="ipr-wrap ipr-intake-grid">
          <div>{body}</div>
          {load === 'ready' && order && <OrderSummary order={order} refText={ref} paid={paid} />}
        </div>
      </section>
    </IpIntelShell>
  );
};

// ---------------------------------------------------------------------------

const Message: React.FC<{ eyebrow: string; title: string; text: React.ReactNode; action?: React.ReactNode; from: string }> = ({ eyebrow, title, text, action, from }) => (
  <div style={{ maxWidth: 620 }}>
    <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 14 }}>{eyebrow}</div>
    <h1 className="ipr-h1" style={{ fontSize: 34 }}>{title}</h1>
    <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ipr-text)', margin: '0 0 28px' }}>{text}</p>
    <div className="ipr-cta-row">
      {action}
      <BackLink from={from} />
    </div>
  </div>
);

const OrderSummary: React.FC<{ order: IntakeOrder; refText: string; paid: boolean }> = ({ order, refText, paid }) => (
  <aside className="ipr-card ipr-sticky" style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)' }}>Your order</div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 20, fontWeight: 700, color: 'var(--ipr-heading)' }}>{refText || '—'}</div>
      {paid
        ? <span className="ipr-pill-paid"><span aria-hidden="true">✓</span> Paid</span>
        : <span className="ipr-pill-paid" style={{ background: 'var(--ipr-primary-100)', color: 'var(--ipr-primary-700)' }}>Awaiting payment</span>}
    </div>
    <div style={{ fontSize: 14, color: 'var(--ipr-heading)', fontWeight: 500 }}>
      IP Intelligence Report
      <div style={{ fontSize: 13, color: 'var(--ipr-muted)', fontWeight: 400 }}>One technology</div>
    </div>
    {[
      ['Name', order.name],
      ['Email', order.email],
      ['Organisation', order.organisation],
      ['Amount paid', order.total_minor != null && paid ? `${money(order.total_minor, order.currency || 'INR')} incl. tax` : ''],
      ['Paid on', fmtDate(order.paid_at)],
      ['Report due', ['intake_submitted', 'in_progress'].includes(order.status) ? fmtDate(order.due_at) : ''],
      ['Status', statusLabel(order.status, order.status_label)],
    ].filter(([, v]) => !!v).map(([k, v]) => (
      <div key={k} style={{ borderTop: '1px solid var(--ipr-border)', paddingTop: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--ipr-muted)', marginBottom: 4 }}>{k}</div>
        <div style={{ fontSize: 15, color: 'var(--ipr-heading)', fontWeight: 500, wordBreak: 'break-word' }}>{v}</div>
      </div>
    ))}
    <div style={{ borderTop: '1px solid var(--ipr-border)', paddingTop: 12, fontSize: 13, color: 'var(--ipr-gray-300)', lineHeight: 1.55 }}>
      Questions? <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`IP Intel Report ${refText}`)}`} style={{ color: 'var(--ipr-primary-600)', fontWeight: 600 }}
        onClick={() => ipTrack('ipintel_click_contact', { position: 'intake', order_ref: refText })}>{CONTACT_EMAIL}</a>
    </div>
  </aside>
);

const statusLabel = (s: string, serverLabel?: string): string => ({
  created: 'Awaiting payment',
  paid: 'Paid — waiting for your technology details',
  intake_submitted: 'Details received — clock running',
  clarification: 'We need a few clarifications',
  in_progress: 'In progress',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
} as Record<string, string>)[s] || serverLabel || s;

// ---------------------------------------------------------------------------

const SubmittedPanel: React.FC<{ order: IntakeOrder; onView: () => void }> = ({ order, onView }) => (
  <div className="ipr-card" style={{ padding: 36 }}>
    <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--ipr-success-bg)', color: 'var(--ipr-success-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 20 }} aria-hidden="true">✓</div>
    <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 10 }}>Submission received</div>
    <h1 className="ipr-h1" style={{ fontSize: 32 }}>Your 7-day clock has started</h1>
    <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ipr-text)', margin: '0 0 24px' }}>
      We have your technology details{order.email ? <> and have sent a confirmation to <strong>{order.email}</strong></> : null}.
    </p>
    <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)', marginBottom: 14 }}>What happens next</div>
    <ol style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[
        ['We review your inputs', 'If the brief is thin anywhere, we e-mail you our questions. The clock pauses until we have the answers.'],
        ['We run the seven modules', 'Market sizing, royalty benchmarking, use cases, competition, IP landscape, funding pathway and the GTM recommendation.'],
        ['Your report arrives by e-mail', 'One document, with sources and assumptions stated throughout.'],
      ].map(([t, d], i) => (
        <li key={t} style={{ display: 'flex', gap: 14 }}>
          <div style={{ fontFamily: 'var(--ipr-font-ui)', fontSize: 12, fontWeight: 700, color: 'var(--ipr-primary-500)', minWidth: 22, paddingTop: 3 }}>0{i + 1}</div>
          <div>
            <div style={{ fontFamily: 'var(--ipr-font-display)', fontWeight: 700, fontSize: 16, color: 'var(--ipr-heading)', marginBottom: 2 }}>{t}</div>
            <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ipr-text)' }}>{d}</div>
          </div>
        </li>
      ))}
    </ol>
    <div className="ipr-cta-row">
      <button type="button" className="ipr-btn-primary" onClick={onView}>View your submission</button>
      <BackLink from="submitted" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------

const ReadOnly: React.FC<{ order: IntakeOrder; editable: boolean; onEdit: () => void }> = ({ order, editable, onEdit }) => {
  const i = readIntake(order.intake);
  const trl = i.trl;
  const trlLabel = TRL_LEVELS.find(t => t.n === trl);
  const isCosmos = i.source === 'cosmos';
  const rows: [string, React.ReactNode][] = [
    ['Source', isCosmos ? 'Cosmos listing' : 'Own IP'],
    [isCosmos ? 'Listing' : 'Title', isCosmos ? (i.cosmosTitle || i.cosmosInput || i.cosmosId) : i.title],
    ['University', isCosmos ? i.cosmosUniversity : ''],
    ['Cosmos ID', isCosmos ? i.cosmosId : ''],
    ['Summary', !isCosmos ? i.summary : ''],
    ['Patent / application', !isCosmos ? i.patent : ''],
    ['TRL', trl ? `TRL ${trl}${trlLabel ? ` — ${trlLabel.label}` : ''}` : ''],
    ['Stage of development', i.stage],
    ['Stage notes', i.stageNotes],
    ['Target markets', i.markets],
    ['Key questions', i.questions],
    ['Documents', order.files?.length ? order.files.map(f => `${f.filename}${f.size_bytes ? ` (${mb(f.size_bytes)})` : ''}`).join('\n') : ''],
  ];
  const delivered = order.status === 'delivered';
  return (
    <div>
      <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 14 }}>
        {delivered ? 'Report delivered' : 'Step 2 of 2 · technology details'}
      </div>
      <h1 className="ipr-h1" style={{ fontSize: 36 }}>
        {delivered ? 'Your report is ready' : order.intake ? 'Your submission' : 'Waiting for your technology details'}
      </h1>
      <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ipr-text)', margin: '0 0 28px', maxWidth: 620 }}>
        {delivered
          ? <>We have also e-mailed the download link to <strong>{order.email}</strong>. Questions about the report? Write to <MailLink position="intake_delivered" orderRef={order.ref || order.order_ref} subject={`IP Intel Report ${order.ref || order.order_ref || ''}`} />.</>
          : order.intake
            ? <>Received{order.intake_submitted_at ? ` on ${fmtDate(order.intake_submitted_at)}` : ''}. {editable ? 'You can still change it until our team starts work.' : 'Our team is working from this — to change anything, reply to our e-mail.'}</>
            : <>This order can no longer be edited here. Write to <MailLink position="intake_locked" orderRef={order.ref || order.order_ref} subject={`IP Intel Report ${order.ref || order.order_ref || ''}`} /> if something needs to change.</>}
      </p>
      {delivered && order.delivery_url && (
        <a className="ipr-btn-primary" href={order.delivery_url} style={{ marginBottom: 28 }}
          onClick={() => ipTrack('ipintel_click_delivery_download', { order_ref: order.ref || order.order_ref })}>
          Download your report
        </a>
      )}
      {order.intake && (
        <div className="ipr-card" style={{ padding: 28 }}>
          <dl className="ipr-dl" style={{ margin: 0 }}>
            {rows.filter(([, v]) => v !== '' && v != null).map(([k, v]) => (
              <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>
            ))}
          </dl>
        </div>
      )}
      {editable && (
        <button type="button" className="ipr-btn-secondary" style={{ marginTop: 20 }} onClick={() => { ipTrack('ipintel_intake_edit', { status: order.status }); onEdit(); }}>
          Edit submission
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------

interface FormProps {
  token: string;
  order: IntakeOrder;
  onSubmitted: () => void | Promise<void>;
  onCancelEdit?: () => void;
}

const IntakeForm: React.FC<FormProps> = ({ token, order, onSubmitted, onCancelEdit }) => {
  const prev = readIntake(order.intake);
  const [source, setSource] = useState<Source>(prev.source === 'own' ? 'own' : 'cosmos');
  // Cosmos
  const [query, setQuery] = useState<string>(prev.source === 'cosmos' && !prev.cosmosId ? prev.cosmosInput : '');
  const [picked, setPicked] = useState<TechHit | null>(
    prev.source === 'cosmos' && prev.cosmosId
      ? { utech_technology_id: prev.cosmosId, title: prev.cosmosTitle || prev.cosmosId, university_name: prev.cosmosUniversity }
      : null,
  );
  const [hits, setHits] = useState<TechHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [showHits, setShowHits] = useState(false);
  // Own
  const [title, setTitle] = useState<string>(prev.title);
  const [summary, setSummary] = useState<string>(prev.summary);
  const [patent, setPatent] = useState<string>(prev.patent);
  // Both
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [over, setOver] = useState(false);
  const [trl, setTrl] = useState<string>(prev.trl ? String(prev.trl) : '');
  const [stage, setStage] = useState<string>(prev.stage);
  const [stageNotes, setStageNotes] = useState<string>(prev.stageNotes);
  const [markets, setMarkets] = useState<string>(prev.markets);
  const [questions, setQuestions] = useState<string>(prev.questions);
  const [confirm, setConfirm] = useState<boolean>(prev.confirm && !!order.intake);
  const [hp, setHp] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [fileNote, setFileNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const existingFiles = order.files || [];
  // Already-uploaded files the visitor chose to drop on this resubmission.
  const [dropped, setDropped] = useState<Record<string, boolean>>({});
  const kept = existingFiles.filter(f => !(f.id && dropped[f.id]));
  const maxFiles = order.limits?.max_files || UPLOAD_MAX_FILES;
  const maxMb = order.limits?.max_file_mb || UPLOAD_MAX_MB;
  const exts = order.limits?.allowed_extensions?.length ? order.limits.allowed_extensions : UPLOAD_EXTS;
  const stages = order.stage_options?.length ? order.stage_options : STAGES;
  const isClarification = order.status === 'clarification';

  // ---- Cosmos lookup (debounced, cancels the previous request)
  useEffect(() => {
    if (source !== 'cosmos' || picked) return;
    const q = query.trim();
    if (q.length < 2 || /^https?:\/\//i.test(q)) { setHits([]); setSearching(false); return; }
    const ctl = new AbortController();
    setSearching(true);
    const t = setTimeout(async () => {
      const res = await ipIntelApi.techLookup(q, ctl.signal);
      if (ctl.signal.aborted) return;
      setSearching(false);
      setHits(res.ok ? techHits(res.data).slice(0, 10) : []);
      setShowHits(true);
    }, 250);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [query, source, picked]);

  const chooseSource = (s: Source) => {
    if (s === source) return;
    setSource(s);
    setErrors({});
    ipTrack('ipintel_intake_source', { source: s });
  };

  // ---- files
  const addFiles = (list: FileList | null) => {
    if (!list || !list.length) return;
    const notes: string[] = [];
    const next = [...files];
    Array.from(list).forEach(f => {
      const ext = extOf(f.name);
      const sizeMb = f.size / 1024 / 1024;
      const reject = (reason: string, note: string) => {
        notes.push(note);
        ipTrack('ipintel_intake_file_rejected', { reason, ext: ipClip(ext, 12), mb: Math.round(sizeMb * 10) / 10 });
      };
      if (!exts.includes(ext)) { reject('type', `${f.name}: .${ext || '?'} files are not accepted.`); return; }
      if (sizeMb > maxMb) { reject('size', `${f.name} is ${sizeMb.toFixed(1)} MB — the limit is ${maxMb} MB per file.`); return; }
      if (kept.length + next.length >= maxFiles) { reject('count', `Up to ${maxFiles} files in total — ${f.name} was not added.`); return; }
      if (next.some(p => p.file.name === f.name && p.file.size === f.size)) return;
      next.push({ id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`, file: f, progress: 0 });
      ipTrack('ipintel_intake_file_added', { ext, mb: Math.round(sizeMb * 10) / 10 });
    });
    setFiles(next);
    setFileNote(notes.join(' '));
    if (errors.files) setErrors(e => ({ ...e, files: '' }));
  };
  const removeFile = (id: string) => {
    const f = files.find(x => x.id === id);
    if (f) ipTrack('ipintel_intake_file_removed', { ext: ipClip(extOf(f.file.name), 12), existing: false });
    setFiles(fs => fs.filter(x => x.id !== id));
  };

  // ---- validate + submit
  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (source === 'cosmos') {
      const q = query.trim();
      if (!picked && !q) e.cosmos = 'Search for the listing, or paste its link or UTECH id.';
      else if (!picked && !looksLikeRef(q)) e.cosmos = 'Pick a listing from the results, or paste its link or UTECH id (like UTECH-IITB-000123).';
    } else {
      if (title.trim().length < 3) e.title = 'Give the technology a title.';
      if (summary.trim().length < 20) e.summary = 'A few sentences, please — what it is and what it does (20 characters at least).';
      if (!files.length && !kept.length) e.files = 'Upload at least one document — a patent, application, disclosure or dossier.';
    }
    if (!trl) e.trl = 'TRL is compulsory.';
    if (!stage) e.stage = 'Stage of development is compulsory.';
    else if (stage === 'Other' && !stageNotes.trim()) e['stage-notes'] = 'Describe the stage in a few words.';
    if (!confirm) e.confirm = 'Please confirm how we can reach you.';
    return e;
  };

  const submit = async () => {
    if (submittingRef.current) return;
    const e = validate();
    setErrors(e);
    setFormError('');
    if (Object.keys(e).length) {
      ipTrack('ipintel_intake_error', { error: 'validation', fields: Object.keys(e).join(',') });
      const first = Object.keys(e)[0];
      document.getElementById(`ipr-in-${first}`)?.focus();
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    const payload: IntakePayload = {
      source,
      ...(source === 'cosmos'
        ? {
          utech_technology_id: picked?.utech_technology_id,
          cosmos_ref: picked ? picked.utech_technology_id : query.trim(),
        }
        : { title: title.trim(), summary: summary.trim(), patent_number: patent.trim() || undefined }),
      trl: Number(trl),
      stage_of_development: stage,
      stage_notes: stageNotes.trim() || undefined,
      target_markets: markets.trim() || undefined,
      key_questions: questions.trim() || undefined,
      contact_confirm: true,
      // Only when something was removed: omitted, the server keeps every file.
      ...(existingFiles.some(f => f.id && dropped[f.id]) ? { keep_file_ids: kept.map(f => f.id!).filter(Boolean) } : {}),
      website: hp,
    };
    ipTrack('ipintel_intake_submit', { source, trl: Number(trl), files: files.length, order_ref: order.ref || order.order_ref });

    // Per-file progress from one multipart body: files go up in the order
    // they were appended, so map loaded bytes onto their cumulative sizes.
    const sizes = files.map(f => f.file.size);
    const sum = sizes.reduce((a, b) => a + b, 0) || 1;
    const res = await submitIntake(token, payload, files.map(f => f.file), (loaded, total) => {
      const sent = (loaded / (total || 1)) * sum;
      let start = 0;
      setFiles(fs => fs.map((f, idx) => {
        const s = start; start += sizes[idx];
        return { ...f, progress: Math.max(0, Math.min(1, (sent - s) / (sizes[idx] || 1))) };
      }));
    });
    submittingRef.current = false;
    setSubmitting(false);
    if (!res.ok) {
      ipTrack('ipintel_intake_error', { error: ipClip(res.error), status: res.status });
      setFiles(fs => fs.map(f => ({ ...f, progress: 0 })));
      setFormError(res.error);
      return;
    }
    ipTrack('ipintel_intake_success', { order_ref: order.ref || order.order_ref, source, clarification: isClarification, files: files.length });
    await onSubmitted();
  };

  return (
    <form noValidate onSubmit={e => { e.preventDefault(); void submit(); }}>
      <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 14 }}>Step 2 of 2 · technology details</div>
      <h1 className="ipr-h1" style={{ fontSize: 36 }}>{isClarification ? 'A few clarifications' : 'Tell us about the technology'}</h1>
      <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ipr-text)', margin: '0 0 28px', maxWidth: 620 }}>
        {isClarification
          ? 'Answer our questions below — update any field or add documents — and send it back. The clock resumes once we have your answers.'
          : 'One technology per report. TRL and stage of development are compulsory — without them the analysis cannot be benchmarked. Your 7-day clock starts once the inputs are complete.'}
      </p>

      {isClarification && order.clarification_notes && (
        <div className="ipr-alert ipr-alert-info" style={{ marginBottom: 20, fontSize: 14 }}>
          <strong style={{ display: 'block', marginBottom: 4 }}>Our questions</strong>
          <span style={{ whiteSpace: 'pre-wrap' }}>{order.clarification_notes}</span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* ---------------- 1. The technology ---------------- */}
        <Card title="The technology" hint="A listing already on Cosmos, or your own IP.">
          <div className="ipr-seg" role="group" aria-label="Technology source">
            <button type="button" aria-pressed={source === 'cosmos'} onClick={() => chooseSource('cosmos')}>Cosmos listing</button>
            <button type="button" aria-pressed={source === 'own'} onClick={() => chooseSource('own')}>Own IP</button>
          </div>

          {source === 'cosmos' ? (
            <div style={{ position: 'relative' }}>
              <label className="ipr-label" htmlFor="ipr-in-cosmos">Find the listing</label>
              {picked ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, border: '1px solid var(--ipr-primary-300)', background: 'var(--ipr-primary-100)', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--ipr-font-display)', fontWeight: 700, fontSize: 15, color: 'var(--ipr-heading)' }}>
                      <span aria-hidden="true" style={{ color: 'var(--ipr-success-400)', marginRight: 6 }}>✓</span>{picked.title}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ipr-gray-300)' }}>{[picked.university_name, picked.utech_technology_id].filter(Boolean).join(' · ')}</div>
                  </div>
                  <button type="button" className="ipr-link-underline" style={{ border: 'none', borderBottom: '1px solid var(--ipr-gray-400)', flexShrink: 0 }}
                    onClick={() => { ipTrack('ipintel_intake_cosmos_change'); setPicked(null); setQuery(''); setTimeout(() => document.getElementById('ipr-in-cosmos')?.focus(), 0); }}>Change</button>
                </div>
              ) : (
                <>
                  <input
                    id="ipr-in-cosmos"
                    className={`ipr-input${errors.cosmos ? ' is-invalid' : ''}`}
                    placeholder="Search by title, or paste a Cosmos link / UTECH id"
                    value={query}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={showHits && hits.length > 0}
                    aria-controls="ipr-cosmos-hits"
                    aria-describedby="ipr-in-cosmos-hint"
                    onChange={e => { setQuery(e.target.value); setShowHits(true); if (errors.cosmos) setErrors(x => ({ ...x, cosmos: '' })); }}
                    onFocus={() => setShowHits(true)}
                    onBlur={() => setTimeout(() => setShowHits(false), 150)}
                    disabled={submitting}
                  />
                  {showHits && query.trim().length >= 2 && !/^https?:\/\//i.test(query.trim()) && (
                    <ul id="ipr-cosmos-hits" role="listbox" className="ipr-card" style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, marginTop: 6, listStyle: 'none', padding: 6, maxHeight: 300, overflowY: 'auto' }}>
                      {searching && <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ipr-gray-300)' }}>Searching Cosmos…</li>}
                      {!searching && !hits.length && (
                        <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ipr-gray-300)' }}>
                          No listing matches “{query.trim()}”. Paste its link or ID instead, or switch to Own IP.
                        </li>
                      )}
                      {!searching && hits.map(h => (
                        <li key={h.utech_technology_id} role="option" aria-selected={false}>
                          <button type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => {
                              ipTrack('ipintel_intake_cosmos_pick', { utech_technology_id: ipClip(h.utech_technology_id, 60), rank: hits.indexOf(h) + 1, query_len: query.trim().length });
                              setPicked(h); setShowHits(false); if (errors.cosmos) setErrors(x => ({ ...x, cosmos: '' }));
                            }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: 6, padding: '10px 12px', cursor: 'pointer' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--ipr-gray-50)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none'; }}
                          >
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ipr-heading)' }}>{h.title}</div>
                            {h.university_name && <div style={{ fontSize: 12, color: 'var(--ipr-gray-300)' }}>{h.university_name}</div>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div id="ipr-in-cosmos-hint" className="ipr-hint">Type at least two letters to search, or paste the listing's link or UTECH id (like UTECH-IITB-000123).</div>
                </>
              )}
              <FieldErr id="ipr-in-cosmos-err" msg={errors.cosmos} />
            </div>
          ) : (
            <>
              <div>
                <label className="ipr-label" htmlFor="ipr-in-title">Title</label>
                <input id="ipr-in-title" className={`ipr-input${errors.title ? ' is-invalid' : ''}`} value={title} maxLength={200}
                  placeholder="e.g. Cement-free geopolymer concrete" onChange={e => setTitle(e.target.value)} disabled={submitting} />
                <FieldErr id="ipr-in-title-err" msg={errors.title} />
              </div>
              <div>
                <label className="ipr-label" htmlFor="ipr-in-summary">Summary</label>
                <textarea id="ipr-in-summary" className={`ipr-input${errors.summary ? ' is-invalid' : ''}`} value={summary} rows={5} maxLength={4000}
                  placeholder="What it is, the problem it solves, and the key results so far." onChange={e => setSummary(e.target.value)} disabled={submitting} />
                <FieldErr id="ipr-in-summary-err" msg={errors.summary} />
              </div>
              <div>
                <label className="ipr-label" htmlFor="ipr-in-patent">Patent / application number <span className="ipr-opt">(optional)</span></label>
                <input id="ipr-in-patent" className="ipr-input" value={patent} maxLength={120}
                  placeholder="e.g. IN 585523 or 202441015300" onChange={e => setPatent(e.target.value)} disabled={submitting} />
              </div>
            </>
          )}
        </Card>

        {/* ---------------- 2. Documents ---------------- */}
        <Card
          title="Supporting documents"
          hint={source === 'own'
            ? 'Required for your own IP: the patent, application, disclosure or dossier.'
            : 'Optional for a Cosmos listing — add anything that is not on the listing.'}
        >
          <div
            id="ipr-in-files"
            tabIndex={0}
            role="button"
            aria-label="Add documents"
            className={`ipr-drop${over ? ' is-over' : ''}`}
            onClick={() => fileInput.current?.click()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current?.click(); } }}
            onDragOver={e => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={e => { e.preventDefault(); setOver(false); if (!submitting) addFiles(e.dataTransfer.files); }}
            style={errors.files ? { borderColor: 'var(--ipr-danger)' } : undefined}
          >
            <div style={{ fontFamily: 'var(--ipr-font-display)', fontWeight: 700, fontSize: 15, color: 'var(--ipr-primary-700)' }}>
              Drop files here or <span style={{ textDecoration: 'underline' }}>browse</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ipr-primary-600)', marginTop: 6 }}>
              PDF, Word, PowerPoint, Excel, images or ZIP · up to {maxFiles} files · {maxMb} MB each
            </div>
            <input ref={fileInput} type="file" multiple hidden
              accept={exts.map(x => `.${x}`).join(',')}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          </div>
          <FieldErr id="ipr-in-files-err" msg={errors.files} />
          {fileNote && <div className="ipr-alert ipr-alert-error" role="alert">{fileNote}</div>}

          {(files.length > 0 || existingFiles.length > 0) && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {existingFiles.map(f => {
                const gone = !!(f.id && dropped[f.id]);
                return (
                  <li key={`x-${f.id || f.filename}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 14, border: '1px solid var(--ipr-border)', borderRadius: 8, padding: '10px 12px', opacity: gone ? 0.55 : 1 }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ipr-heading)', textDecoration: gone ? 'line-through' : 'none' }}>{f.filename}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: gone ? 'var(--ipr-gray-300)' : 'var(--ipr-success-400)', whiteSpace: 'nowrap' }}>
                        {gone ? 'Will be removed' : `Received${f.size_bytes ? ` · ${mb(f.size_bytes)}` : ''}`}
                      </span>
                      {f.id && !submitting && (
                        <button type="button" onClick={() => {
                          ipTrack(gone ? 'ipintel_intake_file_kept' : 'ipintel_intake_file_removed', { ext: ipClip(extOf(f.filename), 12), existing: true });
                          setDropped(d => ({ ...d, [f.id!]: !gone }));
                        }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ipr-primary-600)', fontSize: 12, fontWeight: 600, textDecoration: 'underline', padding: 2 }}>
                          {gone ? 'Keep' : 'Remove'}
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
              {files.map(f => (
                <li key={f.id} style={{ border: '1px solid var(--ipr-border)', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 14 }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ipr-heading)' }}>{f.file.name}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: 'var(--ipr-gray-300)' }}>
                        {submitting ? `${Math.round(f.progress * 100)}%` : mb(f.file.size)}
                      </span>
                      {!submitting && (
                        <button type="button" onClick={() => removeFile(f.id)} aria-label={`Remove ${f.file.name}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ipr-gray-300)', fontSize: 18, lineHeight: 1, padding: 2 }}>×</button>
                      )}
                    </span>
                  </div>
                  {submitting && <div className="ipr-progress" style={{ marginTop: 8 }}><div style={{ width: `${Math.round(f.progress * 100)}%` }} /></div>}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* ---------------- 3. Readiness ---------------- */}
        <Card title="Readiness" hint="Both are compulsory — they are what the market, royalty and funding analysis is benchmarked against.">
          <div>
            <label className="ipr-label" htmlFor="ipr-in-trl">Technology readiness level (TRL)</label>
            <select id="ipr-in-trl" className={`ipr-input${errors.trl ? ' is-invalid' : ''}`} value={trl}
              onChange={e => { setTrl(e.target.value); if (errors.trl) setErrors(x => ({ ...x, trl: '' })); }} disabled={submitting}>
              <option value="">Select a TRL…</option>
              {TRL_LEVELS.map(t => <option key={t.n} value={t.n}>TRL {t.n} — {t.label}</option>)}
            </select>
            {!trl && !errors.trl && <div className="ipr-hint">From TRL 1 (basic principles observed) to TRL 9 (proven in operation).</div>}
            <FieldErr id="ipr-in-trl-err" msg={errors.trl} />
          </div>
          <div>
            <label className="ipr-label" htmlFor="ipr-in-stage">Stage of development</label>
            <select id="ipr-in-stage" className={`ipr-input${errors.stage ? ' is-invalid' : ''}`} value={stage}
              onChange={e => { setStage(e.target.value); if (errors.stage) setErrors(x => ({ ...x, stage: '' })); }} disabled={submitting}>
              <option value="">Select a stage…</option>
              {stages.map(s => <option key={s} value={s}>{s}</option>)}
              <option value="Other">Other (describe below)</option>
              {stage && stage !== 'Other' && !stages.includes(stage) && <option value={stage}>{stage}</option>}
            </select>
            <FieldErr id="ipr-in-stage-err" msg={errors.stage} />
          </div>
          <div>
            <label className="ipr-label" htmlFor="ipr-in-stage-notes">
              Notes on the stage {stage !== 'Other' && <span className="ipr-opt">(optional)</span>}
            </label>
            <textarea id="ipr-in-stage-notes" className={`ipr-input${errors['stage-notes'] ? ' is-invalid' : ''}`} rows={3} maxLength={2000} value={stageNotes}
              placeholder={stage === 'Other' ? 'Describe where the technology is today' : 'e.g. validated at 5 L scale; pilot with a partner planned for Q2'}
              onChange={e => { setStageNotes(e.target.value); if (errors['stage-notes']) setErrors(x => ({ ...x, 'stage-notes': '' })); }} disabled={submitting} />
            <FieldErr id="ipr-in-stage-notes-err" msg={errors['stage-notes']} />
          </div>
        </Card>

        {/* ---------------- 4. Focus ---------------- */}
        <Card title="Focus" hint="Optional, but it sharpens the analysis.">
          <div>
            <label className="ipr-label" htmlFor="ipr-in-markets">Target markets <span className="ipr-opt">(optional)</span></label>
            <input id="ipr-in-markets" className="ipr-input" maxLength={500} value={markets}
              placeholder="e.g. India and EU; construction, precast" onChange={e => setMarkets(e.target.value)} disabled={submitting} />
          </div>
          <div>
            <label className="ipr-label" htmlFor="ipr-in-questions">Key questions <span className="ipr-opt">(optional)</span></label>
            <textarea id="ipr-in-questions" className="ipr-input" rows={3} maxLength={2000} value={questions}
              placeholder="Anything specific the report should answer — e.g. a royalty range for a non-exclusive licence." onChange={e => setQuestions(e.target.value)} disabled={submitting} />
          </div>
        </Card>

        {/* ---------------- 5. Confirm + submit ---------------- */}
        <div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, lineHeight: 1.55, color: 'var(--ipr-text)' }}>
            <input id="ipr-in-confirm" type="checkbox" checked={confirm} disabled={submitting}
              onChange={e => { setConfirm(e.target.checked); if (errors.confirm) setErrors(x => ({ ...x, confirm: '' })); }}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: '#886A44', flexShrink: 0, cursor: 'pointer' }} />
            <span>
              Clarifications and the finished report go to <strong style={{ color: 'var(--ipr-heading)' }}>{order.email || 'the e-mail on this order'}</strong>. I confirm these details are correct.
            </span>
          </label>
          <FieldErr id="ipr-in-confirm-err" msg={errors.confirm} />
        </div>

        <div className="ipr-hp" aria-hidden="true">
          <label htmlFor="ipr-in-website">Website</label>
          <input id="ipr-in-website" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
        </div>

        {formError && <div className="ipr-alert ipr-alert-error" role="alert">{formError}</div>}

        <div className="ipr-cta-row">
          <button type="submit" className="ipr-btn-primary" disabled={submitting}>
            {submitting && <span className="ipr-spin" aria-hidden="true" />}
            {submitting ? (files.length ? 'Uploading…' : 'Submitting…') : isClarification ? 'Send clarifications' : 'Submit and start the clock'}
          </button>
          {onCancelEdit && (
            <button type="button" className="ipr-btn-secondary" onClick={onCancelEdit} disabled={submitting}>Cancel</button>
          )}
        </div>
      </div>
    </form>
  );
};

export default IpIntelIntakePage;
